import { formatUnits } from 'ethers'

import { IBalance } from '@src/types/balance'
import { Address, IToken } from '@src/types/token'
import { bigintPow } from '@src/utils/bigintPow'

export class Balance implements IBalance {
    name: string | undefined
    symbol: string | undefined
    bigintValue: bigint
    decimals: number
    chainId: number
    address: Address

    constructor(balance: IBalance)
    constructor(token: IToken)
    constructor(data: IBalance | IToken) {
        if ('bigintValue' in data) {
            // Initialize from IBalance
            this.name = data.name
            this.symbol = data.symbol
            this.bigintValue = data.bigintValue
            this.decimals = data.decimals
            this.chainId = data.chainId
            this.address = data.address
        } else {
            // Initialize from IToken with 0 value
            this.name = data.name
            this.symbol = data.symbol
            this.bigintValue = BigInt(0) // Set default value as 0
            this.decimals = data.decimals
            this.chainId = data.chainId
            this.address = data.address
        }
    }

    /**
     * Sets a new value for the balance.
     * @param value - The new value to set.
     * @returns The instance of the Balance object for method chaining.
     */
    setValue(value: bigint): this {
        this.bigintValue = value
        return this
    }

    /**
     * Parses a formatted balance string and converts it to a bigint value.
     * @param balanceStr - The formatted balance string.
     * @returns The bigint representation of the balance.
     */
    parseString(balanceStr: string): this {
        // Remove commas from the string
        const sanitizedBalance = balanceStr.replace(/,/g, '')

        // Split into whole and fractional parts
        const [whole, fractional = ''] = sanitizedBalance.split('.')

        // Adjust the fractional part to match the decimals
        const adjustedFractional = fractional.padEnd(this.decimals, '0').substring(0, this.decimals)

        // Combine whole and fractional parts into a single bigint
        const bigintWhole = BigInt(whole || '0')
        const bigintFractional = BigInt(adjustedFractional || '0')

        // Calculate the final bigint value
        this.bigintValue = bigintWhole * BigInt(10 ** this.decimals) + bigintFractional
        return this
    }

    /**
     * Converts the bigintValue to a string with the correct decimals
     * @returns string value
     */
    toString(): string {
        // Convert the bigintValue to a string with the correct decimals
        return formatUnits(this.bigintValue, this.decimals)
    }

    /**
     * Converts the bigintValue to a number
     * @returns formatted numeric value
     */
    toNumber(): number {
        // Convert the bigintValue to a string then into a number
        return +formatUnits(this.bigintValue, this.decimals)
    }

    /**
     * Formats a balance by adding commas for better readability.
     * Respects the maxDecimals parameter to limit fractional digits.
     * @param maxDecimals - Maximum number of decimal places to retain.
     * @returns The formatted balance with commas.
     */
    formatWithCommas(maxDecimals = 18): string {
        // Convert the bigintValue to a string with the correct decimals
        const value = formatUnits(this.bigintValue, this.decimals)

        // Split the value into whole and fractional parts
        const [whole, fractional = ''] = value.split('.')

        // Limit the fractional part to the specified maxDecimals
        const truncatedFractional = fractional.substring(0, maxDecimals)

        // Add commas to the whole part
        const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

        // Return the formatted value with or without the fractional part
        const formatted = truncatedFractional ? `${formattedWhole}.${truncatedFractional}` : formattedWhole

        return this.removeTrailingZeros(formatted)
    }

    /**
     * Formats a balance using magnitudes (e.g., K for thousand, M for million).
     * @returns The formatted balance with magnitude suffix.
     */
    formatWithMagnitudes(maxDecimals = 18): string {
        const number = this.bigintValue
        const scales = ['', 'K', 'M', 'B', 'T']
        const step = BigInt(1000)
        const unit = bigintPow(BigInt(10), BigInt(this.decimals))

        let scaledNumber: bigint | null = null
        let scale = ''

        for (let i = 0; i < scales.length; i++) {
            if (BigInt(number) / BigInt(unit) < bigintPow(step, BigInt(i + 1))) {
                scaledNumber = BigInt(number) / bigintPow(step, BigInt(i))
                scale = scales[i]
                break
            }
        }

        if (scaledNumber === null) {
            return '>999T'
        } else {
            const formatted = this.formatBigIntWithDecimals(scaledNumber, this.decimals)
            const parts = formatted.split('.')

            if (scale === 'K') {
                if (parts[0].length === 1) {
                    return `${parts[0]},${parts[1].substring(0, 3)}.${parts[1].substring(3, 5)}`
                } else {
                    const fixed = `${parts[0]}.${parts[1].substring(0, 2)}`
                    return `${fixed}${scale}`
                }
            } else if (scale !== '') {
                const fixed = `${parts[0]}.${parts[1].substring(0, 2)}`
                return `${fixed}${scale}`
            } else {
                const fixed = `${parts[0]}.${parts[1].substring(0, maxDecimals)}`
                const formatted = `${fixed}${scale}`
                return this.removeTrailingZeros(formatted)
            }
        }
    }

    /**
     * Helper function to remove trailing zeros from a number string.
     * @param value - The number string to process.
     * @returns A string without trailing zeros in the decimal part.
     */
    private removeTrailingZeros(value: string): string {
        if (value.includes('.')) {
            return value.replace(/(\.\d*?[1-9])0+$/g, '$1').replace(/\.0+$/, '')
        }
        return value
    }

    /**
     * Helper function to format a bigint with decimals.
     * @param value - The bigint value to format.
     * @param decimals - The number of decimals for formatting.
     * @returns A string representing the formatted value.
     */
    private formatBigIntWithDecimals(value: bigint, decimals: number): string {
        const wholePart = value / BigInt(10 ** decimals)
        const fractionalPart = value % BigInt(10 ** decimals)

        const fractionalStr = fractionalPart.toString().padStart(decimals, '0')
        return `${wholePart}.${fractionalStr}`
    }
}

export const computeUSDAmount = (token: IToken, tokenAmount: bigint, maxDecimals: number = 4): number | undefined => {
    if (token.price_current !== undefined) {
        const bnum = new Balance(token).setValue(tokenAmount).toNumber()

        let usdAmount: number | undefined = undefined
        const factor = 10 ** maxDecimals
        usdAmount = Math.floor(factor * token.price_current * bnum) / factor

        if (usdAmount < 0) {
            usdAmount = undefined
        }

        return usdAmount
    } else {
        return undefined
    }
}
